import { Dropdown, type MenuElement, MenuItem } from "prosemirror-menu";
import { type MarkType, Schema } from "prosemirror-model";
import { type Command, EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { COLOR_PALETTE } from "./color-palette";

import { t } from "i18n/i18n";
import { ICON_SIZE, TEXT_COLOR_ICON_PATHS } from "mds/utils/toolbar-icons";

const DEFAULT_TEXT_COLOR = "#000000";
const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Returns true if the selection spans text with multiple different color marks.
 */
function selectionHasMultipleColors(state: EditorState, schema: Schema): boolean {
  const colorMarkType = schema.marks.color;
  if (!colorMarkType) return false;

  const { from, to } = state.selection;
  const keys = new Set<string>();
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    const colorMark = colorMarkType.isInSet(node.marks);
    const key = colorMark ? (colorMark.attrs.color as string) : "default";
    keys.add(key);
    if (keys.size > 1) return false;
  });
  return keys.size > 1;
}

/**
 * Get the current text color from the selection
 *
 * @param state - The current editor state
 * @param schema - The ProseMirror schema
 * @returns The hex color string if a color mark is active, or black when mixed/missing
 */
function getCurrentTextColor(state: EditorState, schema: Schema): string {
  if (selectionHasMultipleColors(state, schema)) return DEFAULT_TEXT_COLOR;

  const colorMarkType = schema.marks.color;
  if (colorMarkType) {
    const { $from, empty } = state.selection;
    const marks = empty ? state.storedMarks || $from.marks() : $from.marks();
    const colorMark = colorMarkType.isInSet(marks);
    return colorMark ? (colorMark.attrs.color as string) : DEFAULT_TEXT_COLOR;
  }

  return DEFAULT_TEXT_COLOR;
}

/**
 * Command to apply a text color mark
 *
 * @param colorMarkType - The color mark type from the schema
 * @param color - The hex color string to apply
 * @returns A command that applies the color mark
 */
function applyTextColor(colorMarkType: MarkType, color: string): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    const attrs = { color };

    const tr = empty
      ? // If no text is selected, set the color for future input (stored mark)
        state.tr.addStoredMark(colorMarkType.create(attrs))
      : // Apply the color mark to the selected text
        state.tr.addMark(from, to, colorMarkType.create(attrs));
    if (dispatch) dispatch(tr);
    return true;
  };
}

/**
 * Custom dropdown that updates its icon color based on the current text color
 */
export class DynamicColorPicker implements MenuElement {
  private dropdown: Dropdown;

  private barPathEl: SVGPathElement | null = null;

  private schema: Schema;

  constructor(schema: Schema) {
    this.schema = schema;

    // Create menu items for each color
    const colorSwatches: MenuElement[] = COLOR_PALETTE.map((color) => {
      return new MenuItem({
        label: "",
        render() {
          const swatch = document.createElement("div");
          swatch.className = "ProseMirror-color-swatch";
          swatch.setAttribute("data-color", color);
          swatch.style.backgroundColor = color;
          return swatch;
        },
        run: (state, dispatch) => {
          const colorMarkType = schema.marks.color;
          if (colorMarkType) {
            return applyTextColor(colorMarkType, color)(state, dispatch);
          }
          return false;
        },
      });
    });

    // Create dropdown with color grid
    this.dropdown = new Dropdown(colorSwatches, {
      label: "",
      title: t("worksheet.rich_text_editor.text_color"),
    });
  }

  render(view: EditorView) {
    const { dom, update } = this.dropdown.render(view);

    // Set up the dropdown label
    const dropdownBtn = dom.querySelector(".ProseMirror-menu-dropdown") as HTMLElement;
    if (dropdownBtn) {
      const dropdownLabel = document.createElement("div");
      dropdownLabel.className = "color-picker-dropdown-label";

      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("viewBox", `0 0 ${ICON_SIZE} ${ICON_SIZE}`);
      svg.setAttribute("width", String(ICON_SIZE));
      svg.setAttribute("height", String(ICON_SIZE));

      const aPath = document.createElementNS(SVG_NS, "path");
      aPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[0]);
      aPath.setAttribute("fill", "var(--black-tint-20)");

      const barPath = document.createElementNS(SVG_NS, "path");
      barPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[1]);
      barPath.setAttribute("fill", getCurrentTextColor(view.state, this.schema));

      svg.appendChild(aPath);
      svg.appendChild(barPath);
      dropdownLabel.appendChild(svg);
      dropdownBtn.appendChild(dropdownLabel);
      this.barPathEl = barPath;
    }

    // Wrap the update function in order to update icon color when state changes
    const wrappedUpdate = (state: EditorState) => {
      const result = update(state);
      if (this.barPathEl) {
        this.barPathEl.setAttribute("fill", getCurrentTextColor(state, this.schema));
      }
      return result;
    };

    return { dom, update: wrappedUpdate, focusable: dropdownBtn };
  }
}
